import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  AccountTree as HealthTreeIcon,
  Lock as LockIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  targetPage?: string;
  action?: string;
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

const TOUR_STEPS: TourStep[] = [
  {
    id: 'explore',
    title: 'Step 1: Explore Your Health Decision Tree',
    description: 'First, explore the Health Tree to see all the health choices you can make. Think of it as your decision tree for building your healthiest life!',
    icon: <HealthTreeIcon sx={{ fontSize: 40, color: '#10b981' }} />,
    targetPage: '/talent-tree',
    action: 'Visit Health Tree',
    position: { top: '50%', left: '50%' },
  },
  {
    id: 'lockin',
    title: 'Step 2: Commit to Your Weekly Goals',
    description: 'Once you\'ve explored your options, it\'s time to commit! Use the Weekly Commitment to choose which health talents you\'ll focus on this week. This creates accountability and structure.',
    icon: <LockIcon sx={{ fontSize: 40, color: '#f59e0b' }} />,
    targetPage: '/weekly-lockin',
    action: 'Go to Weekly Commitment',
    position: { top: '50%', left: '50%' },
  },
  {
    id: 'report',
    title: 'Step 3: Track & Report Progress',
    description: 'Finally, report your daily activities and track your progress. You\'ll receive email reminders to help you stay on track. Consistency is key to building lasting health habits!',
    icon: <ReportIcon sx={{ fontSize: 40, color: '#8b5cf6' }} />,
    targetPage: '/weekly-lockin',
    action: 'Track Progress',
    position: { top: '50%', left: '50%' },
  },
  {
    id: 'complete',
    title: 'You\'re All Set! 🎉',
    description: 'That\'s it! You now know the Health-Tree process: Explore → Commit → Track Progress. Start building your healthiest life today!',
    icon: <HealthTreeIcon sx={{ fontSize: 40, color: '#10b981' }} />,
    position: { top: '50%', left: '50%' },
  },
];

interface OnboardingTourProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const OnboardingTour: React.FC<OnboardingTourProps> = ({ open, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { mode } = useThemeMode();

  const currentTourStep = TOUR_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === TOUR_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAction = () => {
    if (currentTourStep.targetPage) {
      navigate(currentTourStep.targetPage);
      onClose();
    }
  };

  const handleComplete = () => {
    // Mark tour as completed for the user
    if (user?.id) {
      // We'll add this to the user profile later
      console.log('Tour completed for user:', user.id);
    }
    onComplete();
    onClose();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!open) return null;

  return (
    <>
      <Backdrop
        open={open}
        sx={{
          zIndex: 9999,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      />
      <Fade in={open}>
        <Box
          sx={{
            position: 'fixed',
            zIndex: 10000,
            transform: 'translate(-50%, -50%)',
            ...currentTourStep.position,
            width: '100%',
            maxWidth: { xs: '90vw', sm: 480 },
          }}
        >
          <Paper
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 3,
              backgroundColor: mode === 'dark' ? '#1f2937' : '#ffffff',
              border: mode === 'dark' ? '1px solid rgba(55, 65, 81, 1)' : '1px solid rgba(229, 231, 235, 1)',
              boxShadow: mode === 'dark' 
                ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
                : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleSkip}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Step Indicator */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              {TOUR_STEPS.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: index === currentStep 
                      ? '#10b981' 
                      : mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                    mx: 0.5,
                    transition: 'background-color 0.3s ease',
                  }}
                />
              ))}
            </Box>

            {/* Icon */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              {currentTourStep.icon}
            </Box>

            {/* Content */}
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                textAlign: 'center',
                color: mode === 'dark' ? '#ffffff' : '#1f2937',
                mb: 2,
              }}
            >
              {currentTourStep.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : 'rgba(31, 41, 55, 0.8)',
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              {currentTourStep.description}
            </Typography>

            {/* Action Button (for steps that have actions) */}
            {currentTourStep.action && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleAction}
                  sx={{
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                  }}
                >
                  {currentTourStep.action}
                </Button>
              </Box>
            )}

            {/* Navigation Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={handleBack}
                disabled={isFirstStep}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                  '&:disabled': {
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                Back
              </Button>

              <Typography
                variant="caption"
                sx={{
                  color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                }}
              >
                {currentStep + 1} of {TOUR_STEPS.length}
              </Typography>

              <Button
                onClick={handleNext}
                endIcon={isLastStep ? null : <ArrowForwardIcon />}
                variant={isLastStep ? 'contained' : 'text'}
                sx={{
                  ...(isLastStep ? {
                    backgroundColor: '#10b981',
                    '&:hover': {
                      backgroundColor: '#059669',
                    },
                  } : {
                    color: '#10b981',
                  }),
                  fontWeight: 'bold',
                }}
              >
                {isLastStep ? 'Get Started!' : 'Next'}
              </Button>
            </Box>

            {/* Skip Button */}
            {!isLastStep && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  onClick={handleSkip}
                  variant="text"
                  size="small"
                  sx={{
                    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',
                    fontSize: '0.875rem',
                  }}
                >
                  Skip Tour
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Fade>
    </>
  );
};

export default OnboardingTour;